import { useMemo } from 'react';
import { Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { aboveEverythingZIndex } from '@/shared/ui/FloatingWindow';
import { DialogFooter } from '@/shared/ui/DialogFooter';
import type { PaymentPreviewAffectedDocument, PaymentPreviewBalanceChange, PaymentPreviewDiff } from '@/entities/payment';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };

export interface PaymentPreviewConsequencesDialogProps {
  diff: PaymentPreviewDiff | undefined;
  onResolve: (confirmed: boolean) => void;
}

const documentColumns: ColumnsType<PaymentPreviewAffectedDocument> = [
  { title: 'Документ', dataIndex: 'label', width: 120 },
  { title: 'Оплачено', width: 160, render: (_v, r) => `${r.paidAmountBefore} → ${r.paidAmountAfter}` },
  {
    title: 'Статус оплаты',
    width: 160,
    render: (_v, r) => `${PAYMENT_STATUS_LABEL[r.paymentStatusBefore] ?? r.paymentStatusBefore} → ${PAYMENT_STATUS_LABEL[r.paymentStatusAfter] ?? r.paymentStatusAfter}`,
  },
];

const balanceColumns: ColumnsType<PaymentPreviewBalanceChange> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName' },
  { title: 'Было', width: 120, align: 'right', render: (_v, r) => r.before },
  { title: 'Станет', width: 120, align: 'right', render: (_v, r) => r.after },
];

/** §8.5: последствия правки/разнесения/удаления платежа — предупреждение, а не блокировка
 * (кнопки «Сохранить»/«Отмена»), тот же паттерн что и `PreviewConsequencesDialog` для документов. */
export function PaymentPreviewConsequencesDialog({ diff, onResolve }: PaymentPreviewConsequencesDialogProps) {
  const isOpen = diff !== undefined;
  const zIndex = useMemo(() => (isOpen ? aboveEverythingZIndex() : undefined), [isOpen]);

  return (
    <Modal
      title="Последствия изменения"
      open={isOpen}
      onCancel={() => onResolve(false)}
      footer={<DialogFooter okText="Сохранить" onOk={() => onResolve(true)} onCancel={() => onResolve(false)} />}
      destroyOnHidden
      width={560}
      zIndex={zIndex}
    >
      {diff && diff.affectedDocuments.length > 0 ? (
        <>
          <Typography.Text strong>Затронутые документы</Typography.Text>
          <Table<PaymentPreviewAffectedDocument>
            size="small"
            rowKey="documentId"
            dataSource={diff.affectedDocuments}
            columns={documentColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
        </>
      ) : null}
      {diff && diff.balance ? (
        <>
          <Typography.Text strong>Баланс контрагента</Typography.Text>
          <Table<PaymentPreviewBalanceChange> size="small" rowKey="counterpartyId" dataSource={[diff.balance]} columns={balanceColumns} pagination={false} />
        </>
      ) : null}
    </Modal>
  );
}
